import React from 'react';
import type { CoinMap } from '../../data/coin-usage-mock';

export function CoinSkyline() {
  return (
    <svg className="cu-sky__svg" viewBox="0 0 460 150" aria-hidden="true">
      <path
        className="cu-sky__mount"
        d="M0 98l36-22 28 16 42-40 34 28 28-34 36 22 40-46 38 30 46-18 40 24 28-16 64 20v68H0z"
      />
      <path className="cu-sky__city" d="M18 118h16v32H18zm22-10h12v42H40zm18 6h20v36H58zm28-16h14v52H86zm20 8h18v44h-18zm26-20h12v64h-12zm18 12h22v52h-22zm28-8h14v60h-14zm22 14h16v46h-16z" />
      <path
        className="cu-sky__path"
        d="M28 108c36-4 52-38 92-36 28 1 40 22 70 16 26-6 34-28 62-32 30-4 48 8 78 2 22-4 36-2 52 8"
        fill="none"
      />
      <Pin x={118} y={58} fill="var(--ml-info, #3b82f6)" />
      <Pin x={188} y={78} fill="var(--ml-warning, #f59e0b)" />
      <Pin x={248} y={62} fill="var(--ml-danger, #ef4444)" />
      <Pin x={312} y={48} fill="var(--ml-success, #22c55e)" />
      <Pin x={368} y={66} fill="var(--ml-primary, #8b5cf6)" />
      <g className="cu-sky__coins" transform="translate(392 78)">
        <ellipse cx="0" cy="22" rx="18" ry="7" fill="var(--ml-warning, #e0a100)" />
        <ellipse cx="0" cy="16" rx="18" ry="7" fill="#f5c542" />
        <ellipse cx="0" cy="10" rx="18" ry="7" fill="var(--ml-accent-soft, #ffe08a)" />
        <ellipse cx="16" cy="16" rx="16" ry="6" fill="var(--ml-warning, #e0a100)" />
        <ellipse cx="16" cy="10" rx="16" ry="6" fill="#ffd56a" />
      </g>
    </svg>
  );
}

function Pin({ x, y, fill }: { x: number; y: number; fill: string }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <path d="M0 0c-8 11-13 18-13 26a13 13 0 0 0 26 0c0-8-5-15-13-26z" fill={fill} />
      <circle cy="24" r="4.5" fill="#fff" />
    </g>
  );
}

export function CoinBalanceArt() {
  return (
    <svg className="cu-balance__art" viewBox="0 0 86 72" aria-hidden="true">
      <ellipse cx="34" cy="50" rx="22" ry="8" fill="var(--ml-warning, #e0a100)" />
      <ellipse cx="34" cy="44" rx="22" ry="8" fill="#f5c542" />
      <ellipse cx="34" cy="38" rx="22" ry="8" fill="var(--ml-accent-soft, #ffe08a)" />
      <ellipse cx="54" cy="42" rx="20" ry="7" fill="var(--ml-warning, #d97706)" />
      <ellipse cx="54" cy="36" rx="20" ry="7" fill="#f6c445" />
      <ellipse cx="54" cy="30" rx="20" ry="7" fill="#ffe29a" />
      <circle cx="58" cy="18" r="11" fill="#f6c445" />
      <circle cx="58" cy="18" r="7" fill="none" stroke="#fff6d8" strokeWidth="2" />
    </svg>
  );
}

export function CoinEmptyArt({ compact = false }: { compact?: boolean }) {
  return (
    <svg
      className={compact ? 'cu-art cu-art--compact' : 'cu-art'}
      viewBox="0 0 420 230"
      aria-hidden="true"
    >
      <rect className="cu-art__sky" width="420" height="230" rx="28" />
      <circle className="cu-art__sun" cx="332" cy="48" r="16" />
      <circle className="cu-art__cloud" cx="78" cy="52" r="12" />
      <circle className="cu-art__cloud" cx="96" cy="46" r="16" />
      <path className="cu-art__hill" d="M0 150c52-30 84 12 138-8 44-16 70 18 118-4 34-16 62 8 96-10 18-8 42 4 68-8v118H0z" />
      <path className="cu-art__hill2" d="M0 186c64-22 96 14 156-4 48-14 74 16 168-12v60H0z" />
      <rect className="cu-art__card" x="118" y="78" width="184" height="96" rx="18" />
      <rect className="cu-art__bar" x="140" y="118" width="16" height="36" rx="6" />
      <rect className="cu-art__bar cu-art__bar--b" x="164" y="104" width="16" height="50" rx="6" />
      <rect className="cu-art__bar cu-art__bar--c" x="188" y="126" width="16" height="28" rx="6" />
      <circle cx="250" cy="118" r="18" fill="none" stroke="#f5b942" strokeWidth="8" />
      <path d="M214 70c-8 12-14 18-14 28a14 14 0 0 0 28 0c0-10-6-16-14-28z" fill="var(--ml-info, #3b6cff)" />
      <circle cx="214" cy="96" r="4" fill="#fff" />
      <ellipse cx="168" cy="168" rx="16" ry="6" fill="var(--ml-warning, #e0a100)" />
      <ellipse cx="168" cy="162" rx="16" ry="6" fill="var(--ml-accent-soft, #ffe08a)" />
    </svg>
  );
}

export function SessionMap({ map, label }: { map: CoinMap; label: string }) {
  if (map === 'none') {
    return (
      <div className="cu-nomap">
        <CoinEmptyArt compact />
        <p>{label}</p>
      </div>
    );
  }

  return (
    <div className="cu-map">
      <p className="visually-hidden">{label}</p>
      {map === 'new-york' && <NewYorkMap />}
      {map === 'delhi' && <DelhiMap />}
      {map === 'route' && <RouteMap />}
      {map === 'joystick' && <JoystickMap />}
      <span className="cu-map__expand" aria-hidden="true">
        <svg viewBox="0 0 16 16" width="14" height="14">
          <path d="M6 2H2v4M10 2h4v4M6 14H2v-4M10 14h4v-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </span>
    </div>
  );
}

function MapPinMark({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <ellipse cx="0" cy="28" rx="7" ry="2.5" fill="rgba(15,23,42,0.18)" />
      <path d="M0 0c-7 10-12 16-12 23a12 12 0 0 0 24 0c0-7-5-13-12-23z" fill="var(--ml-danger, #ef4444)" />
      <circle cy="21" r="4" fill="#fff" />
    </g>
  );
}

function NewYorkMap() {
  return (
    <svg viewBox="0 0 320 180" role="img" aria-label="Map of New York">
      <rect className="cu-map__water" width="320" height="180" />
      <path className="cu-map__land" d="M0 20h120l18 30 10 70-20 40H0z" />
      <path className="cu-map__land" d="M168 8h150v164H150l-8-36 20-28 8-40z" />
      <path className="cu-map__road" d="M40 40h70M30 90h80M20 130h90M190 30v120M230 20v140" />
      <text className="cu-map__label" x="28" y="78">Hoboken</text>
      <text className="cu-map__label" x="22" y="124">Jersey City</text>
      <text className="cu-map__city" x="196" y="96">New York</text>
      <text className="cu-map__label" x="214" y="148">Brooklyn</text>
      <MapPinMark x={236} y={58} />
    </svg>
  );
}

function DelhiMap() {
  return (
    <svg viewBox="0 0 320 180" role="img" aria-label="Map of Delhi">
      <rect className="cu-map__water" width="320" height="180" />
      <path className="cu-map__land" d="M24 28h272l-8 130-40 8H48L24 28z" />
      <path className="cu-map__road" d="M40 70h240M40 110h240M150 30v130" />
      <text className="cu-map__city" x="118" y="86">Delhi</text>
      <text className="cu-map__label" x="200" y="132">Noida</text>
      <text className="cu-map__label" x="48" y="132">Gurugram</text>
      <MapPinMark x={168} y={48} />
    </svg>
  );
}

function RouteMap() {
  return (
    <svg viewBox="0 0 320 180" role="img" aria-label="Map of the saved route">
      <rect className="cu-map__water" width="320" height="180" />
      <path className="cu-map__land" d="M16 24h288v132H16z" />
      <path className="cu-map__route" d="M48 132c40-70 40-10 80-56 28-32 20-8 52-28 28-18 36 10 64-8" />
      <circle cx="48" cy="132" r="7" fill="var(--ml-info, #3b6cff)" />
      <circle cx="128" cy="76" r="7" fill="var(--ml-primary, #7c5cff)" />
      <circle cx="180" cy="48" r="7" fill="#f5a524" />
      <circle cx="244" cy="40" r="7" fill="var(--ml-success, #22c55e)" />
      <text className="cu-map__city" x="108" y="156">Saved route</text>
    </svg>
  );
}

function JoystickMap() {
  return (
    <svg viewBox="0 0 320 180" role="img" aria-label="Map of the joystick path">
      <rect className="cu-map__water" width="320" height="180" />
      <path className="cu-map__land" d="M16 24h288v132H16z" />
      <path className="cu-map__joy" d="M54 120c28-48 8-20 36-52 22-24 10 8 40-8 24-12 16 20 48 4 22-12 28-4 52-28" />
      <circle cx="54" cy="120" r="7" fill="var(--ml-success, #22c55e)" />
      <circle cx="230" cy="36" r="7" fill="var(--ml-success, #15803d)" />
      <text className="cu-map__city" x="108" y="156">Joystick path</text>
    </svg>
  );
}
